<script lang="ts" setup>
import { Comment, Fragment, Text, computed, useSlots, type VNode } from 'vue';
import { buttonBaseClasses, buttonDisplayClasses, buttonSizeClasses, buttonVariantClasses, type ButtonDisplay, type ButtonSize, type ButtonVariant } from './classes';

defineOptions({
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    variant?: ButtonVariant | string;
    size?: ButtonSize;
    display?: ButtonDisplay;
    tag?: string;
    type?: 'button' | 'submit' | 'reset';
    to?: any;
    block?: boolean;
    pill?: boolean;
    square?: boolean;
    squared?: boolean;
    disabled?: boolean;
    icon?: string;
    iconClass?: string | string[] | Record<string, boolean>;
    link?: string;
    loading?: boolean;
    noBorder?: boolean;
    unstyled?: boolean;
    active?: boolean;
    activeClass?: string;
}>(), {
    variant: 'secondary',
    size: 'md',
    display: 'default',
    tag: 'button',
    type: 'button',
    to: null,
    icon: null,
    iconClass: () => ([]),
    link: null,
    activeClass: 'active'
});

const slots = useSlots();
const attrs = useAttrs();

const attrsClasses = computed(() => attrs.class);

function getSlotContent(nodes: VNode[]): VNode[]
{
    return nodes.flatMap((node) =>
    {
        if (node.type === Comment)
            return [];

        if (node.type === Text)
        {
            if (typeof node.children === 'string' && node.children.trim().length > 0)
                return [node];

            return [];
        }

        if (node.type === Fragment && Array.isArray(node.children))
            return getSlotContent(node.children as VNode[]);

        return [node];
    });
}

function isIdeoIconNode(node: VNode)
{
    const type = node.type as any;

    return type?.name === 'IdeoIcon' || type?.['__name'] === 'IdeoIcon';
}

function getDefaultSlotContent()
{
    return getSlotContent(slots.default?.() || []);
}

const rootAttrs = computed(() =>
{
    const result = { ...attrs };

    delete result.class;

    return result;
});

const routeTarget = computed(() => props.to ?? props.link);

const componentTag = computed(() =>
{
    if (routeTarget.value)
        return 'router-link';

    return props.tag;
});

const buttonType = computed(() =>
{
    if (componentTag.value !== 'button')
        return null;

    return props.type;
});

const resolvedVariant = computed<ButtonVariant>(() =>
{
    if (props.variant && props.variant in buttonVariantClasses)
        return props.variant as ButtonVariant;

    return 'secondary';
});

const resolvedSize = computed<ButtonSize>(() =>
{
    if (props.size in buttonSizeClasses)
        return props.size;

    return 'md';
});

const iconOnly = computed(() =>
{
    if (props.square)
        return true;

    if (props.display !== 'default')
        return false;

    const slotContent = getDefaultSlotContent();

    if (props.icon)
        return slotContent.length === 0;

    return slotContent.length === 1 && isIdeoIconNode(slotContent[0]);
});

const customClasses = computed(() =>
{
    if (props.unstyled)
    {
        return {
            [props.activeClass]: props.active,
            'w-full': props.block
        };
    }

    return [
        buttonBaseClasses,
        buttonVariantClasses[resolvedVariant.value],
        props.display === 'default' ? buttonSizeClasses[resolvedSize.value] : '',
        buttonDisplayClasses[props.display ?? 'default'],
        props.variant === 'fancy' && !iconOnly.value ? 'px-3.5' : '',
        props.variant === 'vivid' && props.active ? 'border-[#ff9800] bg-[#ff9800] text-white dark:text-gray-950' : '',
        props.display === 'toolbar' && props.active ? 'bg-[#ff9800] text-white hover:bg-[#ff9800]/90 dark:text-gray-950 dark:hover:bg-[#ff9800]/85' : '',
        {
            [props.activeClass]: props.active,
            'w-full': props.block,
            'rounded-full': props.pill,
            'size-8 max-h-8 min-h-8 max-w-8 min-w-8 flex-none p-0': iconOnly.value,
            'rounded-none': props.squared,
            'border-0': props.noBorder
        }
    ];
});

const iconSize = computed(() =>
{
    switch (resolvedSize.value)
    {
        case 'xs': return 16;
        case 'sm': return 16;
        case 'lg': return 18;
        default: return 16;
    }
});
</script>

<template>
    <component
        :is="componentTag"
        v-bind="rootAttrs"
        :to="routeTarget"
        :type="buttonType"
        :class="[customClasses, attrsClasses]"
        :disabled="disabled || loading"
    >
        <IdeoIcon v-if="icon" :name="icon" :class="['shrink-0', props.display === 'dropdown' || props.display === 'dropdown-inline' ? 'me-0 size-4 min-w-5 self-center' : '', props.iconClass]" :size="iconSize" />
        <slot name="default" />

        <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-inherit">
            <Loader class="size-4" />
        </div>
    </component>
</template>
